import React, {useState} from 'react'
import styled from 'styled-components'
import {AutoSizer, List} from 'react-virtualized'
import {getPlaceHolderImage} from '../../../../utils/helpers'
import Header from '../../../Header/Header'
import HeaderButton from '../../../Header/HeaderButton'
import {SearchInput} from '../../../SearchInput/SearchInput'
import {fetchBuildingPrompt} from '../../../../api/search'
import Spinner from '../../../Spinner/Spinner'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import {useViewFetch} from '../../../../utils/fetch'

const BuildingList = ({onCancel, onBuildingSelect}) => {
  const [searchValue, setSearchValue] = useState('')
  const localeState = useLocaleState()

  const {data: buildings, loading: isLoading} = useViewFetch({
    api: fetchBuildingPrompt,
    parameters: {searchValue: searchValue || null},
    initialState: [],
    onChangeOf: [searchValue]
  })

  const onSearchCancel = () => {}

  const onHeaderCancel = () => onCancel()

  const onBuildingClick = blId => onBuildingSelect({blId})

  const rowRenderer = ({index, key, style}) => {
    let building = buildings[index]
    let imageUrl = building.url || getPlaceHolderImage('building')
    const {name, blId, cityName, cityStateId} = building

    return (
      <div key={key} style={style}>
        <BuildingItem onClick={() => onBuildingClick(blId)}>
          <PhotoContainer>
            <StyledImage src={imageUrl} height={60} width={60} alt="building" />
          </PhotoContainer>
          <BuildingName>
            {name} ({blId})
          </BuildingName>
          <Address>
            {cityName}, {cityStateId}
          </Address>
        </BuildingItem>
      </div>
    )
  }

  const noRowsRenderer = () => (
    <EmptyList>
      <div>{_L({key: 'No Buildings Available', localeState})}</div>
    </EmptyList>
  )

  const ITEM_COUNT = buildings.length
  const ROW_HEIGHT = 97

  return (
    <StyledContainer>
      <StyledHeader>
        <Header
          leftContent={
            <HeaderButton
              label={_L({key: 'Cancel', localeState})}
              onClick={onHeaderCancel}
            />
          }
          centerContent={
            <SearchInput
              value={searchValue}
              onSearchChange={e => setSearchValue(e.target.value)}
              placeholder={_L({key: 'Search for a building', localeState})}
              onCancel={onSearchCancel}
            />
          }
        />
      </StyledHeader>
      <StyledListContainer>
        <HeaderText>{_L({key: 'Select a Building', localeState})}</HeaderText>
        {isLoading ? (
          <SpinnerContainer>
            <Spinner color="#3395fe" />
          </SpinnerContainer>
        ) : (
          <div style={{height: 'calc(100vh - 200px)'}}>
            <AutoSizer>
              {({width, height}) => (
                <StyledList
                  height={height}
                  overscanRowCount={20}
                  rowCount={ITEM_COUNT}
                  rowHeight={ROW_HEIGHT}
                  rowRenderer={rowRenderer}
                  width={width}
                  noRowsRenderer={noRowsRenderer}
                />
              )}
            </AutoSizer>
          </div>
        )}
      </StyledListContainer>
    </StyledContainer>
  )
}

export default BuildingList

const StyledContainer = styled.div`
  height: 100vh;
  width: 100vw;
  background-color: #eff0f3;
`

const StyledListContainer = styled.div`
  height: calc(100vh - 80px);
  width: 100%;
  padding: 24px 240px 24px 240px;
  font-size: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    padding: 24px;
  }

  @media (max-width: 420px) {
    height: calc(100vh - 110px);
    padding: 24px 0 50px 0;
    font-size: 14px;
  }
`

const StyledList = styled(List)`
  width: 75%;
  height: 100%;
  flex: 1;
  overflow-y: scroll;
  background-color: #ffffff;
  border-radius: 6px;

  @media (max-width: 420px) {
    border-radius: 0;
  }
`

const StyledHeader = styled.div`
  height: 80px;
  background-color: green;
`
const HeaderText = styled.div`
  height: 65px;
  font-weight: 600;
  color: #000000;
  font-size: 24px;
  padding-top: 10px;
  padding-bottom: 16px;
  box-sizing: border-box;

  @media (max-width: 420px) {
    font-size: 18px;
    padding-left: 16px;
  }
`
const SpinnerContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`

const BuildingItem = styled.div`
  padding: 18px 24px 18px 24px;
  align-items: center;
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 60px auto 70px;
  grid-template-areas: 'photo name icon' 'photo address icon';
  box-sizing: border-box;
  grid-column-gap: 10px;
  grid-row-gap: 0;
  background: white;
  border-bottom: 1px solid #f3f5fb;
  cursor: pointer;

  &:hover {
    background-color: #f3f9ff;
  }
`

const PhotoContainer = styled.div`
  grid-area: photo;
  box-sizing: border-box;
  height: 60px;
  width: 60px;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledImage = styled.img`
  border-radius: 4px;
`

const BuildingName = styled.div`
  grid-area: name;
  box-sizing: border-box;
  font-weight: bold;
`

const Address = styled.div`
  grid-area: address;
  box-sizing: border-box;
  font-weight: normal;
  color: #a4a9b7;
`

const EmptyList = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  height: 100px;
`
